/**
 * Expanded detail for a report.
 */
import * as React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { FileText, Star, Lock, Users, Shield } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  REMOTE_REPORT_STATUS_CONFIG,
  REMOTE_REPORT_SCORING_STATUS_CONFIG,
  REMOTE_REPORT_SESSION_TYPE_CONFIG,
  REMOTE_REPORT_PARTICIPANT_ROLE_CONFIG,
  ACTIVITES_REALISEES_LABELS,
  REPORT_REMOTE_SCHEMA,
  SCORING_TARGET_LABELS,
  SYNTHESIS_MEETING_TYPE_LABEL,
  TUTORING_REPORT_EVALUATION_FIELDS,
  TUTORING_REPORT_SKILLS_FIELDS,
  SCORING_STUDENT_CRITERIA_FIELDS,
  REMOTE_SUBJECT_CONFIG,
} from '@/components/reports/report-configs';
import {
  partitionSessionParticipants,
  reportIcon,
} from '@/components/reports/remote/report-table-utils';
import { parseSessionDate } from '@/components/sessions/remote/session-table-utils';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailField } from '@/components/common/details/DetailField';
import { EmptyPlaceholder } from '@/components/common/details/EmptyPlaceholder';
import { DetailsCard } from '@/components/common/DetailsCard';
import { resolveLucideIcon } from '@/hooks/useRoleConfig';
import { getRoleLabel } from '@/utils/role-label-utils';
import type {
  ReportDetail,
  ReportScoringEntry,
  ReportParticipant,
  ReportAnimator,
} from '@/types/remote/report.types';

/** CSS color from a role token (`color-*`) or a raw color string. */
function resolveRoleColor(color: string | null | undefined, fallback: string): string {
  if (!color) return fallback;
  if (color.startsWith('color-')) return `var(--${color})`;
  return color;
}

/** Label, icon, and color for a remote report participant role. */
function resolveParticipantRoleBadge(p: ReportParticipant): {
  label: string;
  icon: LucideIcon | undefined;
  color: string;
} {
  const cfg = REMOTE_REPORT_PARTICIPANT_ROLE_CONFIG[p.role];
  const label = p.roleLabel ?? cfg?.label ?? p.role;
  const color = resolveRoleColor(p.roleColor, cfg?.badgeColor ?? 'var(--color-1-nuance-2)');
  const icon = p.roleIcon ? resolveLucideIcon(p.roleIcon) : (cfg?.icon ?? undefined);
  return { label, icon, color };
}

/** Label, icon, and color for a remote report animator badge. */
function resolveAnimatorRoleBadge(a: ReportAnimator): {
  label: string;
  icon: LucideIcon;
  color: string;
} {
  const label = a.roleLabel ?? getRoleLabel('animator');
  const color = resolveRoleColor(a.roleColor, 'var(--color-3-nuance-1)');
  const icon = a.roleIcon ? resolveLucideIcon(a.roleIcon) : Shield;
  return { label, icon, color };
}

/** Avatar plus role badges for one remote report participant. */
function ParticipantCard({
  participant,
  avatarColor,
  avatarInitial,
}: Readonly<{
  participant: ReportParticipant;
  avatarColor: string;
  avatarInitial: string;
}>) {
  const roleBadge = resolveParticipantRoleBadge(participant);
  const showCordRole =
    participant.cordRole != null &&
    participant.cordRole !== '' &&
    participant.cordRole !== roleBadge.label;

  return (
    <div className="flex items-start gap-3">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0"
        style={{ backgroundColor: avatarColor }}
      >
        {participant.displayName?.[0] ?? avatarInitial}
      </div>
      <div className="flex flex-col gap-1 min-w-0">
        <span className="text-sm font-medium text-foreground">{participant.displayName}</span>
        <div className="flex flex-wrap items-center gap-1.5">
          <LabelBadge
            label={roleBadge.label}
            icon={roleBadge.icon}
            variant="ghost"
            color={roleBadge.color}
            size="sm"
          />
          {showCordRole && (
            <LabelBadge
              label={participant.cordRole ?? ''}
              variant="outlined"
              color="var(--color-2-nuance-2)"
              size="sm"
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** Avatar plus role badge for one remote report animator. */
function AnimatorCard({ animator }: Readonly<{ animator: ReportAnimator }>) {
  const roleBadge = resolveAnimatorRoleBadge(animator);
  return (
    <div className="flex items-start gap-3">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0"
        style={{ backgroundColor: 'var(--color-3-nuance-1)' }}
      >
        {animator.displayName?.[0] ?? 'A'}
      </div>
      <div className="flex flex-col gap-1 min-w-0">
        <span className="text-sm font-medium text-foreground">{animator.displayName}</span>
        <LabelBadge
          label={roleBadge.label}
          icon={roleBadge.icon}
          variant="ghost"
          color={roleBadge.color}
          size="sm"
        />
      </div>
    </div>
  );
}

/** Coerce an unknown field value to a display string. */
function safeStr(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

/** Filled-star rating, or N/R when the value is missing. */
function StarDisplay({ value, max = 5 }: Readonly<{ value: unknown; max?: number }>) {
  const n = Number(value);
  if (Number.isNaN(n) || n < 1) return <span className="text-muted-foreground text-sm">N/R</span>;
  const filled = Math.min(n, max);
  return (
    <span className="flex items-center gap-0.5">
      {Array.from({ length: max }, (_, i) => (
        <Star
          key={`star-${i}`}
          className={`w-3.5 h-3.5 ${i < filled ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`}
        />
      ))}
      <span className="ml-1 text-xs text-muted-foreground">
        {n}/{max}
      </span>
    </span>
  );
}

/** Multiline text field, or an italic empty placeholder. */
function TextBlock({ value }: Readonly<{ value: unknown }>) {
  const s = safeStr(value);
  if (!s.trim()) {
    return <span className="text-muted-foreground text-sm italic">Non renseigné</span>;
  }
  return <span className="text-sm whitespace-pre-wrap">{s}</span>;
}

/** Activity chips from a remote report field array. */
function BadgeList({ values }: Readonly<{ values: unknown }>) {
  if (!Array.isArray(values) || values.length === 0) {
    return <span className="text-muted-foreground text-sm italic">Aucune</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((v) => {
        const key = safeStr(v);
        const label = ACTIVITES_REALISEES_LABELS[key] ?? key;
        return (
          <span
            key={key}
            className="px-2 py-0.5 rounded-full text-xs font-medium bg-[color-mix(in_srgb,var(--color-1-nuance-1)_12%,transparent)] text-[var(--color-1-nuance-1)] border border-[color-mix(in_srgb,var(--color-1-nuance-1)_25%,transparent)]"
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

/** Read-only tutoring report body (scores, skills, comments). */
function TutoringReportView({ fields }: Readonly<{ fields: Record<string, unknown> }>) {
  const hasAny = Object.values(fields).some(
    (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0),
  );
  if (!hasAny) return <EmptyPlaceholder message="CR non encore saisi" />;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 p-3 rounded-xl bg-[color-mix(in_srgb,var(--color-1-nuance-1)_8%,transparent)] border border-[color-mix(in_srgb,var(--color-1-nuance-1)_20%,transparent)]">
        <Star className="w-5 h-5 text-[var(--color-1-nuance-1)] shrink-0" />
        <div>
          <div className="text-xs text-muted-foreground mb-0.5">Note globale de la séance</div>
          <StarDisplay value={fields.noteGlobale} />
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Évaluation détaillée
        </p>
        <div className="grid grid-cols-2 gap-2">
          {TUTORING_REPORT_EVALUATION_FIELDS.map(({ key, label }) => (
            <DetailField
              key={key}
              label={label}
              value={<StarDisplay value={fields[key]} />}
              vertical
            />
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Compétences de l'élève
        </p>
        <div className="grid grid-cols-3 gap-2">
          {TUTORING_REPORT_SKILLS_FIELDS.map(({ key, label }) => (
            <DetailField
              key={key}
              label={label}
              value={<StarDisplay value={fields[key]} />}
              vertical
            />
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Contenu
        </p>
        <div className="space-y-2">
          <DetailField
            label="Activités réalisées"
            value={<BadgeList values={fields.activitesRealisees} />}
            vertical
          />
          <DetailField
            label="Matière principale"
            value={
              fields.matierePrincipale ? (
                <LabelBadge
                  label={
                    REMOTE_SUBJECT_CONFIG[fields.matierePrincipale as string]?.label ??
                    (fields.matierePrincipale as string)
                  }
                  icon={REMOTE_SUBJECT_CONFIG[fields.matierePrincipale as string]?.icon}
                  variant={
                    REMOTE_SUBJECT_CONFIG[fields.matierePrincipale as string]?.badgeVariant ??
                    'outlined'
                  }
                  color={REMOTE_SUBJECT_CONFIG[fields.matierePrincipale as string]?.badgeColor}
                />
              ) : (
                <TextBlock value={null} />
              )
            }
            vertical
          />
          {fields.competencesTransversales && (
            <DetailField
              label="Compétences transversales"
              value={<TextBlock value={fields.competencesTransversales} />}
              vertical
            />
          )}
        </div>
      </div>

      {fields.resumeSeance && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Résumé de la séance
          </p>
          <p className="text-sm text-foreground bg-[color-mix(in_srgb,var(--foreground)_4%,transparent)] rounded-lg p-3 whitespace-pre-wrap">
            {safeStr(fields.resumeSeance)}
          </p>
        </div>
      )}

      {fields.observationCoordinateur && (
        <div className="rounded-lg border border-dashed border-[color-mix(in_srgb,var(--color-2-nuance-1)_35%,transparent)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_5%,transparent)] p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Lock className="w-3.5 h-3.5 text-[var(--color-2-nuance-1)]" />
            <span className="text-xs font-semibold text-[var(--color-2-nuance-1)]">
              Observation coordinateur — confidentiel
            </span>
          </div>
          <p className="text-sm text-foreground whitespace-pre-wrap">
            {safeStr(fields.observationCoordinateur)}
          </p>
        </div>
      )}

      {(fields.conseilEleve || fields.messageEleve || fields.messageParent) && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Messages
          </p>
          <div className="space-y-2">
            {fields.conseilEleve && (
              <DetailField
                label="Conseil à l'élève"
                value={<TextBlock value={fields.conseilEleve} />}
                vertical
              />
            )}
            {fields.messageEleve && (
              <DetailField
                label="Message à l'élève"
                value={<TextBlock value={fields.messageEleve} />}
                vertical
              />
            )}
            {fields.messageParent && (
              <DetailField
                label="Message au parent"
                value={<TextBlock value={fields.messageParent} />}
                vertical
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Read-only meeting synthesis (engagement + free text). */
function SynthesisView({
  fields,
  schemaId,
}: Readonly<{ fields: Record<string, unknown>; schemaId: string }>) {
  const synthesis = safeStr(fields.syntheseLibre);
  const hasAny = synthesis.trim() !== '' || fields.niveauEngagement != null;
  if (!hasAny) return <EmptyPlaceholder message="Synthèse non encore saisie" />;

  const typeLabel = SYNTHESIS_MEETING_TYPE_LABEL[schemaId] ?? "d'accueil";

  return (
    <div className="space-y-4">
      <DetailField
        label="Niveau d'engagement des participants"
        value={<StarDisplay value={fields.niveauEngagement} />}
        vertical
      />
      {synthesis.trim() !== '' && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Synthèse de la réunion {typeLabel}
          </p>
          <blockquote className="border-l-4 border-[var(--color-1-nuance-1)] pl-3 text-sm text-foreground whitespace-pre-wrap italic">
            {synthesis}
          </blockquote>
        </div>
      )}
    </div>
  );
}

/** Read-only student scoring criteria and tutor message. */
function ScoringStudentView({ fields }: Readonly<{ fields: Record<string, unknown> }>) {
  const hasAny = Object.values(fields).some((v) => v !== null && v !== undefined && v !== '');
  if (!hasAny) return <EmptyPlaceholder message="Scoring non encore saisi" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-3 rounded-xl bg-[color-mix(in_srgb,var(--color-3-nuance-1)_8%,transparent)] border border-[color-mix(in_srgb,var(--color-3-nuance-1)_20%,transparent)]">
        <Star className="w-5 h-5 text-[var(--color-3-nuance-1)] shrink-0" />
        <div>
          <div className="text-xs text-muted-foreground mb-0.5">Note globale</div>
          <StarDisplay value={fields.noteGlobale} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2">
        {SCORING_STUDENT_CRITERIA_FIELDS.map(({ key, label }) => (
          <DetailField
            key={key}
            label={label}
            value={<StarDisplay value={fields[key]} />}
            vertical
          />
        ))}
      </div>

      {fields.appreciationPrincipale && (
        <DetailField
          label="Appréciation principale"
          value={
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[color-mix(in_srgb,var(--color-3-nuance-1)_12%,transparent)] text-[var(--color-3-nuance-1)] border border-[color-mix(in_srgb,var(--color-3-nuance-1)_25%,transparent)]">
              {safeStr(fields.appreciationPrincipale)}
            </span>
          }
          vertical
        />
      )}

      {fields.messageTuteur && safeStr(fields.messageTuteur).trim() !== '' && (
        <DetailField
          label="Message au tuteur"
          value={<TextBlock value={fields.messageTuteur} />}
          vertical
        />
      )}
    </div>
  );
}

/** Read-only light scoring for information or welcome meetings. */
function ScoringLightView({
  fields,
  schemaId,
}: Readonly<{ fields: Record<string, unknown>; schemaId: string }>) {
  const hasAny = Object.values(fields).some((v) => v !== null && v !== undefined && v !== '');
  if (!hasAny) return <EmptyPlaceholder message="Scoring non encore saisi" />;

  const isInformationMeeting = schemaId === 'scoring_light_ri';

  return (
    <div className="space-y-3">
      <DetailField
        label="Clarté de la réunion"
        value={<StarDisplay value={fields.clarteReunion} />}
        vertical
      />
      {isInformationMeeting ? (
        <DetailField
          label="Utilité de la réunion"
          value={<StarDisplay value={fields.utiliteReunion} />}
          vertical
        />
      ) : (
        <DetailField
          label="Compréhension du fonctionnement"
          value={<StarDisplay value={fields.comprehensionFonctionnement} />}
          vertical
        />
      )}
      {fields.commentaireLibre && safeStr(fields.commentaireLibre).trim() !== '' && (
        <DetailField
          label="Commentaire libre"
          value={<TextBlock value={fields.commentaireLibre} />}
          vertical
        />
      )}
    </div>
  );
}

/** Schema-driven report fields (tutoring / meeting / field-event). */
export function ReportFields({
  schemaId,
  fields,
}: Readonly<{ schemaId: string; fields: Record<string, unknown> }>) {
  if (schemaId === 'cr_tutorat') return <TutoringReportView fields={fields} />;
  if (schemaId === 'synthese_ri' || schemaId === 'synthese_ra') {
    return <SynthesisView fields={fields} schemaId={schemaId} />;
  }
  return <EmptyPlaceholder message={getErrorMessage('unknownReportType')} />;
}

/** Schema-driven scoring fields (student / tutor / meeting). */
export function ScoringContent({
  schemaId,
  fields,
}: Readonly<{ schemaId: string; fields: Record<string, unknown> }>) {
  if (schemaId === 'scoring_eleve') return <ScoringStudentView fields={fields} />;
  if (schemaId === 'scoring_light_ri' || schemaId === 'scoring_light_ra') {
    return <ScoringLightView fields={fields} schemaId={schemaId} />;
  }
  return <EmptyPlaceholder message={getErrorMessage('unknownScoringType')} />;
}

/** Props for {@link ReportDetails}. */
interface ReportDetailsProps {
  report: ReportDetail;
}

/** Expanded remote report details (GET entity). */
export const ReportDetails: React.FC<ReportDetailsProps> = ({ report }) => {
  const { session, reportView, scoringView } = report;

  const reportStatusConfig = REMOTE_REPORT_STATUS_CONFIG[reportView.status];
  const scoringStatusConfig = REMOTE_REPORT_SCORING_STATUS_CONFIG[scoringView.status];
  const sessionTypeConfig = REMOTE_REPORT_SESSION_TYPE_CONFIG[session.type];
  const typeLabel = sessionTypeConfig?.label ?? session.type;
  let reportSectionTitle = 'Compte rendu';
  if (reportView.schemaId === REPORT_REMOTE_SCHEMA.tutoring) {
    reportSectionTitle = `Compte rendu - ${typeLabel}`;
  } else if (
    reportView.schemaId === REPORT_REMOTE_SCHEMA.synthesisInformationMeeting ||
    reportView.schemaId === REPORT_REMOTE_SCHEMA.synthesisWelcomeMeeting
  ) {
    reportSectionTitle = `Synthèse - ${typeLabel}`;
  }

  const allParticipants = session.participants ?? [];
  const { tutors, students, parents, others } = partitionSessionParticipants(allParticipants);

  const sessionDate = parseSessionDate(String(session.date ?? ''));

  const hasParticipants =
    !!session.animator ||
    tutors.length > 0 ||
    students.length > 0 ||
    parents.length > 0 ||
    others.length > 0;

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <FileText className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">
                {sessionTypeConfig?.label ?? 'Compte rendu'}
              </h3>
              {sessionTypeConfig && (
                <LabelBadge
                  label={sessionTypeConfig.label}
                  icon={sessionTypeConfig.icon}
                  variant="ghost"
                  color={sessionTypeConfig.badgeColor}
                  size="sm"
                />
              )}
            </div>
            <p className="details-view-header-subtitle">
              {format(sessionDate, 'EEEE d MMMM yyyy', { locale: fr })}
              {session.startTime && session.endTime && (
                <span className="ml-1">
                  - de {session.startTime.slice(0, 5)} à {session.endTime.slice(0, 5)}
                </span>
              )}
              {(session.programName || session.programId) && (
                <span className="ml-2 text-xs opacity-70">
                  · {session.programName ?? session.programId}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="details-view-grid">
        {hasParticipants && (
          <DetailsCard icon={Users} title="Participants" colorName="color-2" colSpan="full">
            <div className="flex flex-wrap gap-5">
              {session.animator && <AnimatorCard animator={session.animator} />}
              {tutors.map((p) => (
                <ParticipantCard
                  key={p.userId}
                  participant={p}
                  avatarColor="var(--color-1-nuance-1)"
                  avatarInitial="T"
                />
              ))}
              {students.map((p) => (
                <ParticipantCard
                  key={p.userId}
                  participant={p}
                  avatarColor="var(--color-2-nuance-1)"
                  avatarInitial="E"
                />
              ))}
              {parents.map((p) => (
                <ParticipantCard
                  key={p.userId}
                  participant={p}
                  avatarColor="var(--color-3-nuance-1)"
                  avatarInitial="P"
                />
              ))}
              {others.map((p) => (
                <ParticipantCard
                  key={p.userId}
                  participant={p}
                  avatarColor="var(--color-2-nuance-2)"
                  avatarInitial="?"
                />
              ))}
            </div>
          </DetailsCard>
        )}

        <DetailsCard
          icon={reportIcon(reportView.schemaId)}
          title={reportSectionTitle}
          titleExtra={
            reportStatusConfig && (
              <LabelBadge
                label={reportStatusConfig.label}
                icon={reportStatusConfig.icon}
                variant={reportStatusConfig.badgeVariant}
                color={reportStatusConfig.badgeColor}
                size="sm"
              />
            )
          }
          colorName="color-1"
          colSpan="full"
        >
          <ReportFields schemaId={reportView.schemaId} fields={reportView.fields} />
          {reportView.updatedAt && (
            <p className="mt-3 text-xs text-muted-foreground border-t border-border pt-3">
              Dernière mise à jour :{' '}
              {format(new Date(reportView.updatedAt), 'd MMM yyyy à HH:mm', { locale: fr })}
            </p>
          )}
        </DetailsCard>

        {scoringView.entries.length > 0 ? (
          scoringView.entries.map((entry: ReportScoringEntry) => {
            let scoringCardTitle = 'Scoring';
            if (scoringView.schemaId === 'scoring_eleve') {
              scoringCardTitle =
                entry.targetRole === 'student'
                  ? 'Scoring élève'
                  : `Scoring élève — ${SCORING_TARGET_LABELS[entry.targetRole] ?? entry.targetRole}`;
            } else if (
              scoringView.schemaId === 'scoring_light_ri' ||
              scoringView.schemaId === 'scoring_light_ra'
            ) {
              scoringCardTitle = 'Scoring light';
            }
            return (
              <DetailsCard
                key={entry.id}
                icon={Star}
                title={scoringCardTitle}
                titleExtra={
                  scoringStatusConfig && (
                    <LabelBadge
                      label={scoringStatusConfig.label}
                      icon={scoringStatusConfig.icon}
                      variant={scoringStatusConfig.badgeVariant}
                      color={scoringStatusConfig.badgeColor}
                      size="sm"
                    />
                  )
                }
                colorName="color-3"
                colSpan={1}
              >
                <ScoringContent schemaId={scoringView.schemaId} fields={entry.fields} />
              </DetailsCard>
            );
          })
        ) : (
          <DetailsCard icon={Star} title="Scoring" colorName="color-3" colSpan="full">
            <EmptyPlaceholder message="Scoring non encore renseigné" />
          </DetailsCard>
        )}
      </div>
    </>
  );
};
